import { useEffect, useState } from "react";
import {
  cacheKeyFromUrl,
  getCachedPdf,
  putCachedPdf,
} from "@/features/content/pdf-viewer/lib/pdf-cache";

export type PdfDocumentStatus = "loading" | "ready" | "error";

export type PdfDocumentState = {
  status: PdfDocumentStatus;
  /** The fully downloaded file, ready to hand to the viewer. */
  buffer: ArrayBuffer | null;
  /** Download progress 0–100, or `null` while the total size is unknown. */
  progress: number | null;
  /** Re-run the download from scratch (used by the retry button). */
  retry: () => void;
};

/**
 * Fixed window size for each ranged request. The CDN in front of S3 truncates
 * long/open-ended transfers (`bytes=N-`) mid-stream, but small bounded windows
 * (`bytes=start-end`) come back complete every time — so we download the file in
 * these chunks instead of one long stream.
 */
const CHUNK_SIZE = 1 * 1024 * 1024; // 4 MB
/** Per-chunk retries before the whole download is declared failed. */
const MAX_RETRIES_PER_CHUNK = 6;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Pull the total file size out of a `Content-Range: bytes a-b/TOTAL` header. */
const parseTotalSize = (res: Response): number => {
  const match = res.headers.get("Content-Range")?.match(/\/(\d+)\s*$/);
  return match ? Number(match[1]) : NaN;
};

/**
 * Downloads a PDF into memory in small bounded HTTP `Range` chunks, retrying each
 * chunk independently. Unlike a single long download (which the CDN cuts off on a
 * flaky connection), every request is short-lived and self-contained, so a
 * dropped chunk only costs that 4 MB window — not the whole file.
 *
 * The total size is read from the first chunk's `Content-Range` header rather than
 * a `HEAD` request, because the storage only sends CORS headers on ranged GETs.
 *
 * The viewer is only ever fed a complete buffer, so the network never leaves it
 * stuck on a half-loaded document.
 */
export function usePdfDocument(url: string): PdfDocumentState {
  const [status, setStatus] = useState<PdfDocumentStatus>("loading");
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  // Bumping this re-runs the effect — that's the retry trigger.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;

    /**
     * Fetch a single bounded range, retrying on failure or truncation.
     * Returns the bytes plus the file's total size (from `Content-Range`).
     */
    const fetchChunk = async (
      start: number,
      end: number,
    ): Promise<{ data: Uint8Array; total: number }> => {
      let lastError: unknown;

      for (let tries = 0; tries < MAX_RETRIES_PER_CHUNK; tries++) {
        try {
          const res = await fetch(url, {
            headers: { Range: `bytes=${start}-${end}` },
            signal,
          });
          if (res.status !== 206 && res.status !== 200) {
            throw new Error(`HTTP ${res.status}`);
          }

          const parsedTotal = parseTotalSize(res);
          const data = new Uint8Array(await res.arrayBuffer());
          // Without a total (e.g. a 200 that ignored Range) trust what we got.
          const total = Number.isFinite(parsedTotal)
            ? parsedTotal
            : start + data.byteLength;
          // A short chunk means the connection was cut mid-transfer — retry it.
          const expected = Math.min(end, total - 1) - start + 1;
          if (data.byteLength !== expected) {
            throw new Error(`short chunk: got ${data.byteLength}/${expected}`);
          }
          return { data, total };
        } catch (err) {
          if (cancelled || signal.aborted) throw err;
          lastError = err;
          await delay(600 * (tries + 1));
        }
      }
      throw lastError ?? new Error("chunk download failed");
    };

    const run = async () => {
      setStatus("loading");
      setProgress(0);
      setBuffer(null);

      const cacheKey = cacheKeyFromUrl(url);

      try {
        // Fast path: serve a previously downloaded copy straight from IndexedDB.
        const cached = await getCachedPdf(cacheKey);
        if (cancelled) return;
        if (cached) {
          setBuffer(cached);
          setProgress(100);
          setStatus("ready");
          return;
        }

        // The first chunk also tells us the total size via Content-Range.
        const first = await fetchChunk(0, CHUNK_SIZE - 1);
        if (cancelled) return;

        const { total } = first;
        const out = new Uint8Array(total);
        out.set(first.data, 0);
        let received = first.data.byteLength;
        setProgress(Math.min(100, Math.round((received / total) * 100)));

        for (let start = received; start < total; start += CHUNK_SIZE) {
          const end = Math.min(start + CHUNK_SIZE - 1, total - 1);
          const { data } = await fetchChunk(start, end);
          if (cancelled) return;

          out.set(data, start);
          received += data.byteLength;
          setProgress(Math.min(100, Math.round((received / total) * 100)));
        }

        if (cancelled) return;
        setBuffer(out.buffer);
        setProgress(100);
        setStatus("ready");
        // Persist for next time. Best-effort — failures don't block the viewer.
        void putCachedPdf(cacheKey, out.buffer);
      } catch (err) {
        if (cancelled || signal.aborted) return;
        setStatus("error");
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url, attempt]);

  return {
    status,
    buffer,
    progress,
    retry: () => setAttempt((value) => value + 1),
  };
}
